import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarBlank, CaretLeft, CaretRight, CaretDown } from "phosphor-react";
import { AnimatePresence, motion } from "framer-motion";

/* ───────────────────────── Helpers (pure) ───────────────────────── */

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addDays(d, days) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
}

function addMonths(d, months) {
  const nd = new Date(d);
  const day = nd.getDate();
  nd.setDate(1);
  nd.setMonth(nd.getMonth() + months);
  const max = new Date(nd.getFullYear(), nd.getMonth() + 1, 0).getDate();
  nd.setDate(Math.min(day, max));
  return nd;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clampRange(a, b) {
  if (!a || !b) return { start: null, end: null };
  const s = startOfDay(a);
  const e = startOfDay(b);
  return s <= e ? { start: s, end: e } : { start: e, end: s };
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * IMPORTANT: Date-only strings like "2026-01-30" should be parsed as LOCAL date,
 * not via new Date("YYYY-MM-DD") (which is treated as UTC and can shift a day).
 */
function parseISOToLocalDate(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function isValidISODateString(s) {
  return !!parseISOToLocalDate(s);
}

function formatDisplay(d, locale = "en-US") {
  if (!d) return "";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

function monthLabel(d, locale = "en-US") {
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

function rotateWeekdays(base, weekStartsOn) {
  if (!weekStartsOn) return base;
  return base.slice(weekStartsOn).concat(base.slice(0, weekStartsOn));
}

function buildMonthGrid(monthDate, weekStartsOn = 0) {
  const base = startOfMonth(monthDate);
  const firstDow = base.getDay();
  const offset = (firstDow - weekStartsOn + 7) % 7;
  const gridStart = addDays(base, -offset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({
      date,
      inMonth: date.getMonth() === monthDate.getMonth(),
      day: date.getDate(),
    });
  }
  return cells;
}

function monthIndex(d) {
  return d.getFullYear() * 12 + d.getMonth();
}

/* ───────────────────────── Component ───────────────────────── */

export default function DateRangePicker({
  value = { start: "", end: "" },
  onRangeChange,
  className = "",
  locale = "en-US",
  placeholder = "Select date range",
  weekStartsOn = 0,
  showClear = true,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [draftStart, setDraftStart] = useState(() => parseISOToLocalDate(value.start));
  const [draftEnd, setDraftEnd] = useState(() => parseISOToLocalDate(value.end));
  const [hoverDate, setHoverDate] = useState(null);

  const [inputs, setInputs] = useState(() => ({
    since: value.start || "",
    until: value.end || "",
  }));

  const [activePreset, setActivePreset] = useState("custom");

  const [viewMonth, setViewMonth] = useState(() => {
    const s = parseISOToLocalDate(value.start);
    return startOfMonth(s || new Date());
  });

  /* ───────────────────────── Presets ───────────────────────── */

  const PRESETS = useMemo(
    () => [
      { id: "today", label: "Today" },
      { id: "yesterday", label: "Yesterday" },
      { id: "last_7_days", label: "Last 7 days" },
      { id: "last_30_days", label: "Last 30 days" },
      { id: "last_90_days", label: "Last 90 days" },
      { id: "last_365_days", label: "Last 365 days" },
      { id: "week_to_date", label: "Week to date" },
      { id: "month_to_date", label: "Month to date" },
      { id: "year_to_date", label: "Year to date" },
    ],
    []
  );

  const computePresetRange = (id) => {
    const today = startOfDay(new Date());
    const yesterday = addDays(today, -1);
    const dayOfWeek = today.getDay();
    const mondayOffset = (dayOfWeek + 6) % 7;

    switch (id) {
      case "today":
        return { start: today, end: today };
      case "yesterday":
        return { start: yesterday, end: yesterday };
      case "last_7_days":
        return { start: addDays(today, -7), end: yesterday };
      case "last_30_days":
        return { start: addDays(today, -30), end: yesterday };
      case "last_90_days":
        return { start: addDays(today, -90), end: yesterday };
      case "last_365_days":
        return { start: addDays(today, -365), end: yesterday };
      case "week_to_date":
        return { start: addDays(today, -mondayOffset), end: today };
      case "month_to_date":
        return { start: startOfMonth(today), end: today };
      case "year_to_date":
        return { start: new Date(today.getFullYear(), 0, 1), end: today };
      default:
        return { start: addDays(today, -30), end: yesterday };
    }
  };

  const presetIdForRange = (s, e) => {
    if (!s || !e) return "custom";
    for (const p of PRESETS) {
      const r = computePresetRange(p.id);
      if (isSameDay(s, r.start) && isSameDay(e, r.end)) return p.id;
    }
    return "custom";
  };

  /* ───────────────────────── Open/close + sync ───────────────────────── */

  useEffect(() => {
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const s = parseISOToLocalDate(value.start);
    const e = parseISOToLocalDate(value.end);

    setDraftStart(s);
    setDraftEnd(e);
    setHoverDate(null);

    setInputs({
      since: s ? toISO(s) : "",
      until: e ? toISO(e) : "",
    });

    setActivePreset(presetIdForRange(s, e));
    setViewMonth(startOfMonth(s || new Date()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) return;
    const s = parseISOToLocalDate(value.start);
    const e = parseISOToLocalDate(value.end);

    setDraftStart(s);
    setDraftEnd(e);
    setHoverDate(null);
    setInputs({
      since: s ? toISO(s) : "",
      until: e ? toISO(e) : "",
    });
    setActivePreset(presetIdForRange(s, e));
    if (s) setViewMonth(startOfMonth(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.start, value.end]);

  /* ───────────────────────── Range helpers ───────────────────────── */

  const currentRange = useMemo(() => clampRange(draftStart, draftEnd), [draftStart, draftEnd]);

  const inRange = (date) => {
    if (!date || !draftStart) return false;
    const d = startOfDay(date);

    if (draftEnd) {
      const { start, end } = currentRange;
      if (!start || !end) return false;
      return d >= start && d <= end;
    }

    if (hoverDate) {
      const s = startOfDay(draftStart);
      const h = startOfDay(hoverDate);
      const from = s <= h ? s : h;
      const to = s <= h ? h : s;
      return d >= from && d <= to;
    }

    return isSameDay(d, draftStart);
  };

  const isStart = (date) => {
    if (!date || !draftStart) return false;
    const d = startOfDay(date);

    if (draftEnd) {
      const { start } = currentRange;
      return !!start && isSameDay(d, start);
    }

    if (hoverDate) {
      const s = startOfDay(draftStart);
      const h = startOfDay(hoverDate);
      const from = s <= h ? s : h;
      return isSameDay(d, from);
    }

    return isSameDay(d, draftStart);
  };

  const isEnd = (date) => {
    if (!date || !draftStart) return false;
    const d = startOfDay(date);

    if (draftEnd) {
      const { end } = currentRange;
      return !!end && isSameDay(d, end);
    }

    if (hoverDate) {
      const s = startOfDay(draftStart);
      const h = startOfDay(hoverDate);
      const to = s <= h ? h : s;
      return isSameDay(d, to);
    }

    return isSameDay(d, draftStart);
  };

  const keepEndVisible = (maybeEnd) => {
    if (!maybeEnd) return;
    const diff = monthIndex(maybeEnd) - monthIndex(viewMonth);
    if (diff > 1 || diff < 0) setViewMonth(startOfMonth(maybeEnd));
  };

  /* ───────────────────────── Interactions ───────────────────────── */

  const handlePresetClick = (id) => {
    if (id === "custom") {
      setActivePreset("custom");
      return;
    }
    const { start, end } = computePresetRange(id);
    setActivePreset(id);
    setDraftStart(start);
    setDraftEnd(end);
    setHoverDate(null);
    setInputs({ since: toISO(start), until: toISO(end) });
    setViewMonth(startOfMonth(start));
    keepEndVisible(end);
  };

  const onDayClick = (date) => {
    if (!date) return;

    setActivePreset("custom");

    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(date);
      setDraftEnd(null);
      setHoverDate(null);
      setInputs({ since: toISO(date), until: "" });
      setViewMonth(startOfMonth(date));
      return;
    }

    let s = draftStart;
    let e = date;
    if (e < s) [s, e] = [e, s];

    setDraftStart(s);
    setDraftEnd(e);
    setHoverDate(null);
    setInputs({ since: toISO(s), until: toISO(e) });
    keepEndVisible(e);
  };

  const onDayHover = (date) => {
    if (draftStart && !draftEnd) setHoverDate(date);
  };

  const prevMonth = () => setViewMonth((m) => startOfMonth(addMonths(m, -1)));
  const nextMonth = () => setViewMonth((m) => startOfMonth(addMonths(m, 1)));

  const clearDraft = () => {
    setActivePreset("custom");
    setDraftStart(null);
    setDraftEnd(null);
    setHoverDate(null);
    setInputs({ since: "", until: "" });
  };

  const handleSinceChange = (val) => {
    setActivePreset("custom");
    setInputs((p) => ({ ...p, since: val }));
    if (isValidISODateString(val)) {
      const newSince = parseISOToLocalDate(val);
      setDraftStart(newSince);
      setDraftEnd((prevEnd) => (prevEnd && newSince <= prevEnd ? prevEnd : newSince));
      setHoverDate(null);
      setViewMonth(startOfMonth(newSince));
    }
  };

  const handleUntilChange = (val) => {
    setActivePreset("custom");
    setInputs((p) => ({ ...p, until: val }));
    if (isValidISODateString(val)) {
      const newUntil = parseISOToLocalDate(val);
      setDraftEnd(newUntil);
      setDraftStart((prevStart) => (prevStart && newUntil >= prevStart ? prevStart : newUntil));
      setHoverDate(null);
      keepEndVisible(newUntil);
    }
  };

  const handleSinceBlur = () => {
    if (inputs.since && !isValidISODateString(inputs.since)) {
      setInputs((p) => ({ ...p, since: draftStart ? toISO(draftStart) : "" }));
    }
  };

  const handleUntilBlur = () => {
    if (inputs.until && !isValidISODateString(inputs.until)) {
      setInputs((p) => ({ ...p, until: draftEnd ? toISO(draftEnd) : "" }));
    }
  };

  const apply = () => {
    if (!onRangeChange) return setOpen(false);

    const s = draftStart ? toISO(startOfDay(draftStart)) : "";
    const e = draftEnd ? toISO(startOfDay(draftEnd)) : "";

    onRangeChange({ start: s, end: e });
    setOpen(false);
  };

  const cancel = () => {
    const s = parseISOToLocalDate(value.start);
    const e = parseISOToLocalDate(value.end);
    setDraftStart(s);
    setDraftEnd(e);
    setHoverDate(null);
    setInputs({ since: s ? toISO(s) : "", until: e ? toISO(e) : "" });
    setActivePreset(presetIdForRange(s, e));
    setOpen(false);
  };

  /* ───────────────────────── Trigger label ───────────────────────── */

  const triggerLabel = useMemo(() => {
    const committedStart = parseISOToLocalDate(value.start);
    const committedEnd = parseISOToLocalDate(value.end);

    if (!committedStart || !committedEnd) return placeholder;

    const pid = presetIdForRange(committedStart, committedEnd);
    if (pid !== "custom") return PRESETS.find((x) => x.id === pid)?.label || placeholder;

    return `${formatDisplay(committedStart, locale)} – ${formatDisplay(committedEnd, locale)}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.start, value.end, locale, placeholder]);

  /* ───────────────────────── Calendar rendering ───────────────────────── */

  const daysHeader = useMemo(() => {
    const base = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    return rotateWeekdays(base, weekStartsOn);
  }, [weekStartsOn]);

  const DayCell = ({ cell }) => {
    const date = cell.date;
    const inM = cell.inMonth;
    const inR = inRange(date);
    const s = isStart(date);
    const e = isEnd(date);
    const single = s && e;
    const today = isSameDay(date, new Date());
    const muted = !inM;

    let rangeBg = "";
    if (inR) {
      if (single) rangeBg = "left-0.5 right-0.5 rounded-full";
      else if (s) rangeBg = "left-0.5 right-0 rounded-l-full";
      else if (e) rangeBg = "left-0 right-0.5 rounded-r-full";
      else rangeBg = "left-0 right-0";
    }

    return (
      <div
        className="relative flex items-center justify-center h-8"
        onMouseEnter={() => onDayHover(date)}
      >
        {inR && (
          <div className={`absolute inset-y-1 ${rangeBg} bg-yellow-50 dark:bg-yellow-900/30`} />
        )}

        <button
          type="button"
          onClick={() => onDayClick(date)}
          className={[
            "relative z-10 w-8 h-8 rounded-full text-[11px] transition-colors",
            "flex items-center justify-center",
            s || e
              ? "bg-yellow-600 text-white"
              : inR
              ? "text-yellow-800 dark:text-yellow-100"
              : "hover:bg-neutral-100 dark:hover:bg-neutral-900",
            muted && !(s || e)
              ? "text-neutral-300 dark:text-neutral-600"
              : "text-neutral-800 dark:text-neutral-100",
            today && !inR && !(s || e) ? "ring-1 ring-yellow-400/70" : "",
            "focus:outline-none focus:ring-2 focus:ring-yellow-400/30",
          ].join(" ")}
        >
          {cell.day}
        </button>
      </div>
    );
  };

  const renderMonth = (offset, { showNav } = { showNav: false }) => {
    const m = addMonths(viewMonth, offset);
    const cells = buildMonthGrid(m, weekStartsOn);

    return (
      <div className="w-[280px] max-w-[44vw]">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-semibold text-neutral-800 dark:text-neutral-100">
            {monthLabel(m, locale)}
          </div>

          {showNav ? (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={prevMonth}
                className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-900"
                aria-label="Previous month"
              >
                <CaretLeft size={14} className="text-neutral-600 dark:text-neutral-400" />
              </button>
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-900"
                aria-label="Next month"
              >
                <CaretRight size={14} className="text-neutral-600 dark:text-neutral-400" />
              </button>
            </div>
          ) : (
            <div className="w-[60px]" />
          )}
        </div>

        <div className="grid grid-cols-7 text-center text-[10px] font-medium text-neutral-400 dark:text-neutral-500">
          {daysHeader.map((d) => (
            <div key={d} className="py-0.5">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => (
            <DayCell key={`${toISO(cell.date)}_${idx}`} cell={cell} />
          ))}
        </div>
      </div>
    );
  };

  const presetLabel = (id) => PRESETS.find((p) => p.id === id)?.label || "Custom";

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {/* Compact trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "w-full h-10 px-2.5  min-w-72 rounded-xl",
          "border border-neutral-100 dark:border-neutral-900",
          "bg-white dark:bg-neutral-950",
          "text-[13px] text-neutral-600 dark:text-neutral-100",
          "inline-flex items-center gap-2",
          "hover:bg-neutral-50 dark:hover:bg-neutral-900/40",
          "focus:outline-none focus:ring-2 focus:ring-yellow-400/25",
        ].join(" ")}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarBlank size={15} className="text-neutral-700 dark:text-neutral-400" />
        <span className="truncate">{triggerLabel}</span>
        <span className="ml-auto">
          <CaretDown size={15} className="text-neutral-700 dark:text-neutral-400" />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className={[
              "absolute mt-2 text-start z-50",
              "w-[750px] max-w-[96vw]",
              "bg-white dark:bg-neutral-950",
              "border border-neutral-100 dark:border-neutral-900",
              "rounded-2xl shadow-2xl overflow-hidden",
            ].join(" ")}
            role="dialog"
          >
            <div className="flex">
              {/* Left presets: narrower + tighter */}
              <div className="hidden md:block w-48 border-r border-neutral-100 dark:border-neutral-900 p-2.5">
                <div className="text-xs font-[400] text-neutral-600 dark:text-neutral-300 mt-1 mb-3">
                  Date range
                </div>

                <div className="max-h-[280px] overflow-y-auto pr-1 custom-scroll">
                  <button
                    type="button"
                    onClick={() => handlePresetClick("custom")}
                    className={[
                      "w-full text-left px-2.5 py-1.5 rounded-lg text-[12px] mb-1 transition",
                      activePreset === "custom"
                        ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                        : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900",
                    ].join(" ")}
                  >
                    Custom
                  </button>

                  <div className="my-2 border-t border-neutral-100 dark:border-neutral-900" />

                  {PRESETS.map((p) => {
                    const active = activePreset === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handlePresetClick(p.id)}
                        className={[
                          "w-full text-left whitespace-nowrap px-2.5 py-1.5 rounded-lg text-[12px] mb-1 transition",
                          active
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-900",
                        ].join(" ")}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: tighter layout */}
              <div className="flex-1 p-3">
                {/* Mobile preset */}
                <div className="md:hidden mb-2.5">
                  <div className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                    Date range
                  </div>
                  <div className="relative">
                    <select
                      value={activePreset}
                      onChange={(e) => handlePresetClick(e.target.value)}
                      className={[
                        "w-full h-9 rounded-xl px-2.5 pr-8 text-[13px]",
                        "border border-neutral-100 dark:border-neutral-900",
                        "bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-100",
                        "focus:outline-none focus:ring-2 focus:ring-yellow-400/25",
                      ].join(" ")}
                    >
                      <option value="custom">Custom</option>
                      {PRESETS.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                      <CaretDown size={14} className="text-neutral-600 dark:text-neutral-400" />
                    </div>
                  </div>
                </div>

                {/* Inputs: smaller */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1">
                    <div className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Since
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                        <CalendarBlank size={13} className="text-neutral-500 dark:text-neutral-400" />
                      </div>
                      <input
                        value={inputs.since}
                        onChange={(e) => handleSinceChange(e.target.value)}
                        onBlur={handleSinceBlur}
                        placeholder="YYYY-MM-DD"
                        className={[
                          "w-full h-9 rounded-xl pl-8 pr-2.5 text-[13px]",
                          "border border-neutral-100 dark:border-neutral-900",
                          "bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-100",
                          "focus:outline-none focus:ring-2 focus:ring-yellow-400/25",
                        ].join(" ")}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="mt-6 text-neutral-400 text-[11px]">→</div>

                  <div className="flex-1">
                    <div className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      Until
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none">
                        <CalendarBlank size={13} className="text-neutral-500 dark:text-neutral-400" />
                      </div>
                      <input
                        value={inputs.until}
                        onChange={(e) => handleUntilChange(e.target.value)}
                        onBlur={handleUntilBlur}
                        placeholder="YYYY-MM-DD"
                        className={[
                          "w-full h-9 rounded-xl pl-8 pr-2.5 text-[13px]",
                          "border border-neutral-100 dark:border-neutral-900",
                          "bg-white dark:bg-neutral-950 text-neutral-800 dark:text-neutral-100",
                          "focus:outline-none focus:ring-2 focus:ring-yellow-400/25",
                        ].join(" ")}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {showClear && (
                    <button
                      type="button"
                      onClick={clearDraft}
                      className={[
                        "mt-6 h-9 px-2.5 rounded-xl text-[12px]",
                        "border border-neutral-100 dark:border-neutral-900",
                        "text-neutral-700 dark:text-neutral-200",
                        "hover:bg-neutral-100 dark:hover:bg-neutral-900",
                      ].join(" ")}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Calendars: smaller width + shorter */}
                <div className="flex gap-4">
                  {renderMonth(0, { showNav: true })}
                  <div className="hidden lg:block">{renderMonth(1)}</div>
                </div>

                {/* Footer: tighter */}
                <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-900 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancel}
                    className="text-[12px] px-2.5 py-1.5 rounded-lg text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={!draftStart || !draftEnd}
                    onClick={apply}
                    className={[
                      "text-[12px] px-3.5 py-1.5 rounded-lg font-semibold transition",
                      draftStart && draftEnd
                        ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black hover:scale-[1.01]"
                        : "bg-neutral-200 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 cursor-not-allowed",
                    ].join(" ")}
                  >
                    Apply
                  </button>
                </div>

                <div className="mt-1.5 text-[10px] text-neutral-500 dark:text-neutral-400">
                  Selected: <span className="font-medium">{presetLabel(activePreset)}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
